import {Center} from 'src/design/atoms/Center';
import {Loader} from 'src/design/atoms/Loader';
import {Paper} from 'src/design/atoms/Paper';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {Title} from 'src/design/atoms/Title';
import {useDisclosure} from '@mantine/hooks';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {useParams} from 'react-router-dom';
import {
  FeatureFlagEnum,
  ModuleEnum,
  PermissionOperationEnum,
  WorkflowStateEnum,
} from 'src/api/ProactApiClient';
import type {BreadcrumbItem} from 'src/app/layout/BackBar';
import {BackBar} from 'src/app/layout/BackBar';
import {ContentWrapper} from 'src/app/layout/ContentWrapper';
import {DetailGrid} from 'src/design/atoms/DetailGrid';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {ConfirmDialog} from 'src/design/molecules/ConfirmDialog';

import {SidebarLinkWorkflows} from 'src/app/layout/Sidebar/SidebarLinksConfig';
import {routeRegistry} from 'src/pages/RouteRegistry';
import {
  useGetWorkflowQuery,
  useReprocessWorkflowMutation,
} from 'src/redux/queries/workflow';
import {useLazyGetNexusGraphQuery} from 'src/redux/queries/workflowNexus';
import {useModulePermissions} from 'src/shared/auth/permissions/useModulePermissions';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {useAlert} from 'src/shared/hooks/useAlert';
import useSetDocumentTitle from 'src/shared/hooks/useSetDocumentTitle';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {
  WorkflowDocumentTitle,
  WorkflowHistoryPillConfig,
} from 'src/app/workflows/workflowHelpers';
import {NexusGraph} from '../WorkflowsDash/components/NexusGraph';
import {generateNexusSummarySegments} from '../WorkflowsDash/components/nexusSummary';
import {WorkflowExecutionTimeline} from './components/WorkflowExecutionTimeline';

/**
 * Build navigation for workflow instance page.
 * This is a 3-level navigation: Workflows -> Definition -> Instance
 */
const buildWorkflowInstanceNavigation = (
  definitionId: string,
  definitionName?: string,
  instanceIdentifier?: string
): {
  breadcrumbs: BreadcrumbItem[];
  fallbackTo: string;
  fallbackLabel: string;
} => {
  return {
    breadcrumbs: [
      {label: 'Workflows', to: PathUtility.WorkflowsDashboard('nexus')},
      {
        label: definitionName ?? 'Definition',
        to: PathUtility.WorkflowViewDefinition(definitionId),
      },
      {label: instanceIdentifier ?? 'Instance'},
    ],
    fallbackTo: PathUtility.WorkflowViewDefinition(definitionId),
    fallbackLabel: 'Back to Workflow Definition',
  };
};

export const ViewWorkflowPage = () => {
  useSetDocumentTitle(WorkflowDocumentTitle);
  const {definitionId, id} = useParams();
  const {setAlert} = useAlert();
  const [triggerNexusGraph, {data: nexusData, isFetching: isNexusLoading}] =
    useLazyGetNexusGraphQuery();
  const [reprocessWorkflow, {isLoading: isReprocessing}] =
    useReprocessWorkflowMutation();
  const [confirmOpened, confirmHandlers] = useDisclosure(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const {update: hasUpdatePermission} = useModulePermissions(
    ModuleEnum.Workflows
  );

  const workflowId = parseInt(id ?? '-1');

  const {data, isLoading, refetch} = useGetWorkflowQuery(workflowId, {
    skip: workflowId < 0,
  });

  const handleReprocess = useCallback(async () => {
    confirmHandlers.close();
    try {
      await reprocessWorkflow(workflowId).unwrap();
      setAlert(
        'Workflow has been enqueued for reprocessing. This may take some time to complete.',
        'success'
      );
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
  }, [workflowId, reprocessWorkflow, setAlert, confirmHandlers]);

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      await refetch();
    } finally {
      setIsRefreshing(false);
    }
  }, [refetch]);

  useEffect(() => {
    if (!data) return;
    if (workflowId < 0) return;
    void triggerNexusGraph({
      entityType: 'workflow',
      entityId: workflowId,
      maxDepth: 5,
    });
  }, [data, workflowId, triggerNexusGraph]);

  const handleRefreshNexus = useCallback(() => {
    if (workflowId >= 0)
      void triggerNexusGraph({
        entityType: 'workflow',
        entityId: workflowId,
        maxDepth: 5,
      });
  }, [triggerNexusGraph, workflowId]);

  const navigation = useMemo(
    () =>
      buildWorkflowInstanceNavigation(
        definitionId as string,
        data?.workflowDefinition?.name,
        data?.internalIdentifier
      ),
    [definitionId, data?.workflowDefinition?.name, data?.internalIdentifier]
  );

  const pills = useMemo(
    () => WorkflowHistoryPillConfig(data?.state ?? WorkflowStateEnum.Invalid),
    [data?.state]
  );

  const summarySegments = useMemo(
    () => (nexusData ? generateNexusSummarySegments(nexusData) : []),
    [nexusData]
  );

  if (isLoading) {
    return (
      <ContentWrapper featureFlag={FeatureFlagEnum.Workflows}>
        <Center py="xl">
          <Loader size="lg" />
        </Center>
      </ContentWrapper>
    );
  }

  return (
    <ContentWrapper featureFlag={FeatureFlagEnum.Workflows}>
      <Stack gap="lg">
        <BackBar
          fallbackTo={navigation.fallbackTo}
          fallbackLabel={navigation.fallbackLabel}
          breadcrumbs={navigation.breadcrumbs}
        />

        <PageHeader>
          <PageHeader.Title
            icon={SidebarLinkWorkflows.icon}
            subtitle={data?.internalIdentifier}>
            {data?.workflowDefinition?.name ?? 'Workflow Instance'}
          </PageHeader.Title>

          <PageHeader.Badges>
            {pills.map((pill, index) => (
              <PageHeader.Badge key={index} color={pill.color} variant="light">
                {pill.text}
              </PageHeader.Badge>
            ))}
          </PageHeader.Badges>

          <PageHeader.Actions>
            <PageHeader.Action
              variant="refresh"
              loading={isRefreshing}
              onClick={handleRefresh}>
              Refresh
            </PageHeader.Action>
            {hasUpdatePermission &&
              data?.state !== WorkflowStateEnum.Completed &&
              data?.state !== WorkflowStateEnum.Cancelled && (
                <PageHeader.Action
                  loading={isReprocessing}
                  onClick={() => confirmHandlers.open()}>
                  Reprocess
                </PageHeader.Action>
              )}
          </PageHeader.Actions>
        </PageHeader>

        <DetailGrid>
          <DetailGrid.Main>
            {summarySegments.length > 0 && (
              <Paper>
                <Title order={6}>Summary</Title>
                <Text size="sm" c="dimmed">
                  {summarySegments.map((seg, i) =>
                    seg.emphasis ? (
                      <Text key={i} span fs="italic">
                        {seg.text}
                      </Text>
                    ) : (
                      seg.text
                    )
                  )}
                </Text>
              </Paper>
            )}
            <NexusGraph
              data={nexusData ?? null}
              isLoading={isNexusLoading}
              onRefresh={handleRefreshNexus}
            />
          </DetailGrid.Main>
          <DetailGrid.Sidebar>
            {data && <WorkflowExecutionTimeline workflow={data} />}
          </DetailGrid.Sidebar>
        </DetailGrid>
      </Stack>

      <ConfirmDialog
        opened={confirmOpened}
        onClose={() => confirmHandlers.close()}
        onConfirm={handleReprocess}
        title="Reprocess Workflow"
        body="This will reprocess the workflow's current step. Use this to recover a stuck workflow if the related task has been completed and the workflow has not updated after at least one hour. Completed actions are not reprocessed. Pending / Waiting actions may be completed. Continue?"
        buttonTextYes="Reprocess"
      />
    </ContentWrapper>
  );
};

// Register the route with the new system
routeRegistry.registerPageRoute({
  backNavigation: 'page',
  path: PathUtility.WorkflowView(':definitionId', ':id'),
  element: <ViewWorkflowPage />,
  module: ModuleEnum.Workflows,
  operation: PermissionOperationEnum.View,
  isPrivate: true,
});

export default ViewWorkflowPage;
